import type {
  PlantSpeciesFilter,
  PlantSpeciesSearchResponse,
  PlantVarietyFilter,
  PlantVarietySearchResponse,
} from '../types';

export interface VerdantlyClientConfig {
  apiKey: string;
  baseUrl?: string;
}

export class VerdantlyClient {
  private apiKey: string;
  private baseUrl: string;

  constructor(config: VerdantlyClientConfig) {
    if (!config.apiKey) {
      throw new Error('API Key is required to initialize VerdantlyClient.');
    }

    this.apiKey = config.apiKey;
    this.baseUrl = config.baseUrl || 'https://verdantly-gardening-api.p.rapidapi.com';
  }

  async getPlantVarietyCategories(): Promise<string[]> {
    return this.apiRequest('/v1/plants/varieties/categories');
  }

  async getTypesByCategory(category: string): Promise<string[]> {
    return this.apiRequest(`/v1/plants/varieties/types/${category}`);
  }

  async getSubtypesByType(type: string): Promise<string[]> {
    return this.apiRequest(`/v1/plants/varieties/subtypes/${type}`);
  }

  async searchPlantVarietiesByName(
    query: string,
    page: number = 1
  ): Promise<PlantVarietySearchResponse> {
    return this.apiRequest(`/v1/plants/varieties/name?q=${encodeURIComponent(query)}&page=${page}`);
  }

  async searchPlantVarietiesByFilter(
    filters: PlantVarietyFilter,
    page: number = 1
  ): Promise<PlantVarietySearchResponse> {
    const queryParams = new URLSearchParams({
      ...Object.entries(filters).reduce(
        (acc, [key, value]) => {
          if (value !== undefined && value !== null) acc[key] = String(value);
          return acc;
        },
        {} as Record<string, string>
      ),
      page: page.toString(),
    }).toString();

    return this.apiRequest(`/v1/plants/varieties/filter?${queryParams}`);
  }

  async searchPlantSpeciesByName(
    query: string,
    page: number = 1
  ): Promise<PlantSpeciesSearchResponse> {
    return this.apiRequest(`/v1/plants/species/name?q=${encodeURIComponent(query)}&page=${page}`);
  }

  async searchPlantSpeciesByFilter(
    filters: PlantSpeciesFilter,
    page: number = 1
  ): Promise<PlantSpeciesSearchResponse> {
    const queryParams = new URLSearchParams({
      ...Object.entries(filters).reduce(
        (acc, [key, value]) => {
          if (value !== undefined && value !== null) acc[key] = String(value);
          return acc;
        },
        {} as Record<string, string>
      ),
      page: page.toString(),
    }).toString();

    return this.apiRequest(`/v1/plants/species/filter?${queryParams}`);
  }

  private async apiRequest<T>(
    endpoint: string,
    method: 'GET' | 'POST' = 'GET',
    body?: any
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'x-rapidapi-key': this.apiKey,
      'x-rapidapi-host': 'verdantly-gardening-api.p.rapidapi.com',
    };

    const response = await fetch(url, {
      method,
      headers,
      ...(method === 'POST' ? { body: JSON.stringify(body) } : {}),
    });

    if (!response.ok) {
      const message = await response.text();
      throw new Error(`Request failed: ${response.status} ${response.statusText}\n${message}`);
    }

    return response.json();
  }
}
